import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Image, Paperclip, Send, Trash2, X, Pencil } from 'lucide-react'

import { apiConfig } from '../config/authConfig'
import { apiClient } from '../services/api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface LocaleContent {
  title: string
  content: string
}

interface AttachedImage {
  id: string
  dataUrl: string      // base64 data URL for display
  base64: string       // raw base64 (no data: prefix) for API
  mediaType: string
  name: string
}

interface ContentSuggestion {
  id: string
  section_key: string
  sort_order: number
  action: 'update' | 'create'
  fi: LocaleContent
  en: LocaleContent
  status: 'pending' | 'accepted' | 'rejected'
  contextImages?: AttachedImage[]   // images attached by user when they sent this request
  // Editable copies
  fi_edit?: LocaleContent
  en_edit?: LocaleContent
  editing?: boolean
}

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
  images?: AttachedImage[]
  suggestions: ContentSuggestion[]
  isStreaming: boolean
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface AiHelpChatProps {
  onContentSaved: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Extract unique ![name] image references from content string. */
function extractImageNames(content: string): string[] {
  const matches = content.match(/!\[([^\]]+)\]/g) ?? []
  return [...new Set(matches.map((m) => m.slice(2, -1)))]
}

/** Convert base64 string + mime type to a Blob for FormData upload. */
function base64ToBlob(base64: string, mimeType: string): Blob {
  const bytes = atob(base64)
  const arr = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
  return new Blob([arr], { type: mimeType })
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AiHelpChat({ onContentSaved }: AiHelpChatProps) {
  const { t } = useTranslation('help')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [pendingImages, setPendingImages] = useState<AttachedImage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Welcome message on mount
  useEffect(() => {
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        text: t('aiWelcome'),
        suggestions: [],
        isStreaming: false,
      },
    ])
  }, [t])

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // ── Image handling ──────────────────────────────────────────────────────────

  const addImage = useCallback((file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      alert('Kuva on liian suuri (max 5 MB)')
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string
      const [header, base64] = dataUrl.split(',')
      const mediaType = header.match(/data:([^;]+)/)?.[1] ?? 'image/png'
      setPendingImages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          dataUrl,
          base64,
          mediaType,
          name: file.name,
        },
      ])
    }
    reader.readAsDataURL(file)
  }, [])

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (file) addImage(file)
        }
      }
    },
    [addImage],
  )

  // ── Send message ────────────────────────────────────────────────────────────

  const send = useCallback(async () => {
    const text = input.trim()
    if (!text && pendingImages.length === 0) return
    if (isStreaming) return

    // Capture images before clearing (they'll be attached to suggestions)
    const capturedImages = [...pendingImages]

    // Build content: text + images
    const userContent: Array<{ type: string; text?: string; source?: object }> = []
    if (text) userContent.push({ type: 'text', text })
    for (const img of capturedImages) {
      userContent.push({
        type: 'image',
        source: { type: 'base64', media_type: img.mediaType, data: img.base64 },
      })
    }

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text,
      images: capturedImages.length > 0 ? capturedImages : undefined,
      suggestions: [],
      isStreaming: false,
    }

    const assistantMsgId = crypto.randomUUID()
    const assistantMsg: ChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      text: '',
      suggestions: [],
      isStreaming: true,
    }

    setMessages((prev) => [...prev, userMsg, assistantMsg])
    setInput('')
    setPendingImages([])
    setIsStreaming(true)

    try {
      const token = await apiClient.getToken()

      // Build conversation history for API
      const history = messages
        .filter((m) => m.id !== 'welcome')
        .map((m) => ({
          role: m.role,
          content:
            m.role === 'user' && m.images && m.images.length > 0
              ? [
                  ...(m.text ? [{ type: 'text', text: m.text }] : []),
                  ...m.images.map((img) => ({
                    type: 'image',
                    source: { type: 'base64', media_type: img.mediaType, data: img.base64 },
                  })),
                ]
              : m.text,
        }))

      const body = {
        messages: [...history, { role: 'user', content: userContent }],
        screenshot_names: [],
      }

      const res = await fetch(`${apiConfig.baseUrl}/help/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      })

      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`)
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let sseBuffer = ''

      let readResult = await reader.read()
      while (!readResult.done) {
        const { value } = readResult

        sseBuffer += decoder.decode(value, { stream: true })
        const events = sseBuffer.split('\n\n')
        sseBuffer = events.pop() ?? ''

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue
          const eventMatch = eventBlock.match(/^event: (\w+)/)
          if (!eventMatch) continue
          const dataLines = [...eventBlock.matchAll(/^data: (.*)/gm)].map((m) => m[1])
          if (dataLines.length === 0) continue

          const eventType = eventMatch[1]
          const data = dataLines.join('\n')

          if (eventType === 'text') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId ? { ...m, text: m.text + data } : m,
              ),
            )
          } else if (eventType === 'suggestion') {
            try {
              const suggestion = JSON.parse(data) as Omit<
                ContentSuggestion,
                'id' | 'status' | 'contextImages'
              >
              const newSuggestion: ContentSuggestion = {
                ...suggestion,
                id: crypto.randomUUID(),
                status: 'pending',
                // Attach the images the user sent — used for auto-upload on accept
                contextImages: capturedImages.length > 0 ? capturedImages : undefined,
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, suggestions: [...m.suggestions, newSuggestion] }
                    : m,
                ),
              )
            } catch {
              // ignore malformed suggestion
            }
          } else if (eventType === 'done') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId ? { ...m, isStreaming: false } : m,
              ),
            )
          } else if (eventType === 'error') {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsgId
                  ? { ...m, text: m.text + '\n\n⚠️ ' + data, isStreaming: false }
                  : m,
              ),
            )
          }
        }
        readResult = await reader.read()
      }
    } catch (err) {
      console.error('AI chat error:', err)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, text: t('aiError'), isStreaming: false }
            : m,
        ),
      )
    } finally {
      setIsStreaming(false)
    }
  }, [input, pendingImages, isStreaming, messages, t])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault()
      send()
    }
  }

  // ── Suggestion accept/reject ────────────────────────────────────────────────

  const updateSuggestion = (suggestionId: string, updates: Partial<ContentSuggestion>) => {
    setMessages((prev) =>
      prev.map((msg) => ({
        ...msg,
        suggestions: msg.suggestions.map((s) =>
          s.id === suggestionId ? { ...s, ...updates } : s,
        ),
      })),
    )
  }

  const handleAccept = async (
    suggestion: ContentSuggestion,
    imageUploads: Array<{ imageName: string; image: AttachedImage }>,
  ) => {
    const fi = suggestion.editing ? suggestion.fi_edit! : suggestion.fi
    const en = suggestion.editing ? suggestion.en_edit! : suggestion.en

    try {
      // 1. Upload images (if any selected)
      if (imageUploads.length > 0) {
        const token = await apiClient.getToken()
        await Promise.all(
          imageUploads.map(async ({ imageName, image }) => {
            const ext = image.mediaType.split('/')[1] ?? 'png'
            const blob = base64ToBlob(image.base64, image.mediaType)
            const form = new FormData()
            form.append('file', blob, `${imageName}.${ext}`)
            const res = await fetch(`${apiConfig.baseUrl}/help/screenshots/${imageName}`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: form,
            })
            if (!res.ok) throw new Error(`Screenshot upload failed: ${imageName}`)
          }),
        )
      }

      // 2. Save text content (both locales)
      await Promise.all([
        apiClient.put(`/help/content/${suggestion.section_key}/fi`, {
          title: fi.title,
          content: fi.content,
          sort_order: suggestion.sort_order,
          is_active: true,
        }),
        apiClient.put(`/help/content/${suggestion.section_key}/en`, {
          title: en.title,
          content: en.content,
          sort_order: suggestion.sort_order,
          is_active: true,
        }),
      ])

      updateSuggestion(suggestion.id, { status: 'accepted', editing: false })
      onContentSaved()
    } catch (err) {
      console.error('Accept failed:', err)
      throw err  // re-throw so ContentSuggestionCard can show error
    }
  }

  const handleReject = (suggestionId: string) => {
    updateSuggestion(suggestionId, { status: 'rejected' })
  }

  const handleStartEdit = (suggestion: ContentSuggestion) => {
    updateSuggestion(suggestion.id, {
      editing: true,
      fi_edit: { ...suggestion.fi },
      en_edit: { ...suggestion.en },
    })
  }

  const handleCancelEdit = (suggestion: ContentSuggestion) => {
    updateSuggestion(suggestion.id, { editing: false })
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-[700px]">
      {/* Message list */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-1 pb-4">
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            onAccept={handleAccept}
            onReject={handleReject}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onUpdateSuggestion={updateSuggestion}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Pending image previews */}
      {pendingImages.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
          {pendingImages.map((img) => (
            <div key={img.id} className="relative group">
              <img
                src={img.dataUrl}
                alt={img.name}
                className="h-16 w-16 object-cover rounded border border-gray-300"
              />
              <button
                onClick={() =>
                  setPendingImages((prev) => prev.filter((i) => i.id !== img.id))
                }
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="border-t border-gray-200 pt-3">
        <div className="flex gap-2 items-end">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={t('aiPlaceholder')}
              rows={3}
              disabled={isStreaming}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) addImage(file)
                e.target.value = ''
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming}
              title={t('aiAttachImage')}
              className="p-2 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <button
              onClick={send}
              disabled={isStreaming || (!input.trim() && pendingImages.length === 0)}
              className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
        {isStreaming && (
          <p className="text-xs text-gray-400 mt-1.5 animate-pulse">{t('aiThinking')}</p>
        )}
        {/* Clear chat */}
        {messages.length > 1 && !isStreaming && (
          <button
            onClick={() => {
              setMessages([
                {
                  id: 'welcome',
                  role: 'assistant',
                  text: t('aiWelcome'),
                  suggestions: [],
                  isStreaming: false,
                },
              ])
            }}
            className="mt-2 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" />
            {t('aiClearChat')}
          </button>
        )}
      </div>
    </div>
  )
}

// ─── MessageBubble ────────────────────────────────────────────────────────────

interface MessageBubbleProps {
  message: ChatMessage
  onAccept: (s: ContentSuggestion, imageUploads: Array<{ imageName: string; image: AttachedImage }>) => Promise<void>
  onReject: (id: string) => void
  onStartEdit: (s: ContentSuggestion) => void
  onCancelEdit: (s: ContentSuggestion) => void
  onUpdateSuggestion: (id: string, updates: Partial<ContentSuggestion>) => void
}

function MessageBubble({
  message,
  onAccept,
  onReject,
  onStartEdit,
  onCancelEdit,
  onUpdateSuggestion,
}: MessageBubbleProps) {
  const isUser = message.role === 'user'

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
        {/* Bubble */}
        <div
          className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
            isUser
              ? 'bg-primary-600 text-white rounded-tr-sm'
              : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm'
          }`}
        >
          {message.text}
          {message.isStreaming && (
            <span className="inline-block w-1.5 h-4 bg-gray-400 ml-0.5 animate-pulse rounded-sm" />
          )}
        </div>

        {/* Image thumbnails (user messages) */}
        {message.images && message.images.length > 0 && (
          <div className="flex gap-1 mt-1 justify-end">
            {message.images.map((img) => (
              <img
                key={img.id}
                src={img.dataUrl}
                alt={img.name}
                className="h-12 w-12 object-cover rounded border border-gray-300"
              />
            ))}
          </div>
        )}

        {/* Suggestion cards */}
        {message.suggestions.map((suggestion) => (
          <ContentSuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            onAccept={onAccept}
            onReject={onReject}
            onStartEdit={onStartEdit}
            onCancelEdit={onCancelEdit}
            onUpdateSuggestion={onUpdateSuggestion}
          />
        ))}
      </div>
    </div>
  )
}

// ─── ContentSuggestionCard ────────────────────────────────────────────────────

interface ContentSuggestionCardProps {
  suggestion: ContentSuggestion
  onAccept: (s: ContentSuggestion, imageUploads: Array<{ imageName: string; image: AttachedImage }>) => Promise<void>
  onReject: (id: string) => void
  onStartEdit: (s: ContentSuggestion) => void
  onCancelEdit: (s: ContentSuggestion) => void
  onUpdateSuggestion: (id: string, updates: Partial<ContentSuggestion>) => void
}

function ContentSuggestionCard({
  suggestion,
  onAccept,
  onReject,
  onStartEdit,
  onCancelEdit,
  onUpdateSuggestion,
}: ContentSuggestionCardProps) {
  const { t } = useTranslation('help')
  const isPending = suggestion.status === 'pending'
  const isEditing = suggestion.editing === true
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const fi = isEditing ? suggestion.fi_edit! : suggestion.fi
  const en = isEditing ? suggestion.en_edit! : suggestion.en

  // Detect image names referenced in content (both locales, deduplicated)
  const referencedImageNames = [
    ...new Set([
      ...extractImageNames(fi.content),
      ...extractImageNames(en.content),
    ]),
  ]
  const hasContextImages = (suggestion.contextImages?.length ?? 0) > 0
  const showImageUpload = hasContextImages && referencedImageNames.length > 0

  // Which names to upload (checked by default)
  const [imageChecks, setImageChecks] = useState<Record<string, boolean>>(
    () => Object.fromEntries(referencedImageNames.map((n) => [n, true])),
  )

  const handleAccept = async () => {
    setSaveError(null)
    setIsSaving(true)
    // Build upload list from checked names
    const imageUploads: Array<{ imageName: string; image: AttachedImage }> = []
    if (showImageUpload && suggestion.contextImages) {
      // Use first image for all checked names (common case: 1 screenshot → 1-2 name refs)
      const primaryImage = suggestion.contextImages[0]
      for (const name of referencedImageNames) {
        if (imageChecks[name]) {
          imageUploads.push({ imageName: name, image: primaryImage })
        }
      }
    }
    try {
      await onAccept(suggestion, imageUploads)
    } catch {
      setSaveError(t('aiError'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="mt-3 border border-primary-200 rounded-xl overflow-hidden bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-primary-50 border-b border-primary-200">
        <div className="flex items-center gap-2">
          <span className="text-primary-700 text-sm font-semibold">
            {t('aiSuggestion')}: {suggestion.section_key}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-600 font-medium">
            {suggestion.action === 'update' ? t('aiSuggestionUpdate') : t('aiSuggestionCreate')}
          </span>
        </div>
        {suggestion.status === 'accepted' && (
          <span className="text-xs text-green-600 font-medium flex items-center gap-1">
            <Check className="w-3 h-3" />
            {t('aiAccepted')}
          </span>
        )}
        {suggestion.status === 'rejected' && (
          <span className="text-xs text-gray-400">{t('aiRejected')}</span>
        )}
      </div>

      {/* Bilingual preview — 2 columns */}
      <div className="grid grid-cols-2 divide-x divide-gray-200">
        <LocalePanel
          flag="🇫🇮"
          label="Suomi"
          content={fi}
          isEditing={isEditing}
          onChange={(updates) =>
            onUpdateSuggestion(suggestion.id, {
              fi_edit: { ...suggestion.fi_edit!, ...updates },
            })
          }
        />
        <LocalePanel
          flag="🇬🇧"
          label="English"
          content={en}
          isEditing={isEditing}
          onChange={(updates) =>
            onUpdateSuggestion(suggestion.id, {
              en_edit: { ...suggestion.en_edit!, ...updates },
            })
          }
        />
      </div>

      {/* Image upload section — shown when content references images and user attached images */}
      {isPending && showImageUpload && (
        <div className="px-4 py-3 bg-blue-50 border-t border-blue-100">
          <div className="flex items-center gap-1.5 mb-2">
            <Image className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs font-semibold text-blue-700">
              {t('aiSaveScreenshot')}
            </span>
          </div>
          <div className="flex items-start gap-3">
            {/* Image thumbnail */}
            <img
              src={suggestion.contextImages![0].dataUrl}
              alt="screenshot"
              className="h-14 w-14 object-cover rounded border border-blue-200 flex-shrink-0"
            />
            {/* Checkboxes per referenced name */}
            <div className="space-y-1.5 flex-1">
              {referencedImageNames.map((name) => (
                <label key={name} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={imageChecks[name] ?? true}
                    onChange={(e) =>
                      setImageChecks((prev) => ({ ...prev, [name]: e.target.checked }))
                    }
                    className="rounded border-blue-300 text-blue-600"
                  />
                  <span className="text-xs text-blue-800 font-mono">{name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {isPending && (
        <div className="bg-gray-50 border-t border-gray-200">
          {saveError && (
            <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-b border-red-100">
              {saveError}
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex gap-2">
              {!isEditing ? (
                <button
                  onClick={() => onStartEdit(suggestion)}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-colors"
                >
                  <Pencil className="w-3 h-3" />
                  {t('aiEdit')}
                </button>
              ) : (
                <button
                  onClick={() => onCancelEdit(suggestion)}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-colors"
                >
                  <X className="w-3 h-3" />
                  {t('aiEditCancel')}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onReject(suggestion.id)}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                <X className="w-3 h-3" />
                {t('aiReject')}
              </button>
              <button
                onClick={handleAccept}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors font-medium"
              >
                {isSaving ? (
                  <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Check className="w-3 h-3" />
                )}
                {isSaving ? t('aiSaved') : t('aiAccept')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── LocalePanel ──────────────────────────────────────────────────────────────

interface LocalePanelProps {
  flag: string
  label: string
  content: LocaleContent
  isEditing: boolean
  onChange: (updates: Partial<LocaleContent>) => void
}

function LocalePanel({ flag, label, content, isEditing, onChange }: LocalePanelProps) {
  return (
    <div className="p-3 min-w-0">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-base">{flag}</span>
        <span className="text-xs font-semibold text-gray-600">{label}</span>
      </div>
      {isEditing ? (
        <div className="space-y-2">
          <input
            value={content.title}
            onChange={(e) => onChange({ title: e.target.value })}
            className="w-full px-2 py-1.5 text-xs font-semibold border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
          />
          <textarea
            value={content.content}
            onChange={(e) => onChange({ content: e.target.value })}
            rows={6}
            className="w-full px-2 py-1.5 text-xs font-mono border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 resize-y"
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-gray-800 truncate">{content.title}</p>
          <div className="text-xs text-gray-600 leading-relaxed max-h-48 overflow-y-auto">
            <HelpContentPreview text={content.content} />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── HelpContentPreview ───────────────────────────────────────────────────────
// Renders \n\n → <p> and ![name] → placeholder tag

function HelpContentPreview({ text }: { text: string }) {
  const parts = text.split(/(!\[[^\]]+\])/)
  return (
    <>
      {parts.map((part, i) => {
        const imgMatch = part.match(/^!\[([^\]]+)\]$/)
        if (imgMatch) {
          return (
            <span
              key={i}
              className="inline-block my-1 px-2 py-0.5 text-xs font-mono bg-blue-50 text-blue-500 rounded border border-blue-200"
            >
              🖼 {imgMatch[1]}
            </span>
          )
        }
        return part
          .split('\n\n')
          .filter(Boolean)
          .map((para, j) => (
            <p key={`${i}-${j}`} className="mb-2">
              {para}
            </p>
          ))
      })}
    </>
  )
}
